import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, type TeamRole } from "./api";
import { getSession } from "./auth";

/**
 * The operator's role for the active provider (#50). Shares the cached
 * `["session"]` query the shell already issues, so this costs no extra
 * round-trip. `canWrite` mirrors the backend rule: reads for any member,
 * writes for owner/admin. The server still enforces RBAC — this only
 * decides what controls to render.
 */
export function useOperatorRole(): {
  role: TeamRole | null;
  canWrite: boolean;
  isOwner: boolean;
} {
  const { data } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });
  const role = data?.role ?? null;
  return {
    role,
    canWrite: role === "owner" || role === "admin",
    isOwner: role === "owner",
  };
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: apiClient.health,
    staleTime: 60_000,
  });
}

export function useProvider() {
  return useQuery({
    queryKey: ["provider"],
    queryFn: apiClient.provider.get,
  });
}

export function useAnalytics() {
  return useQuery({
    queryKey: ["analytics"],
    queryFn: apiClient.dashboard.analytics,
  });
}

export function useApiKeys() {
  return useQuery({
    queryKey: ["api-keys"],
    queryFn: apiClient.apiKeys.list,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label?: string) => apiClient.apiKeys.create(label),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => apiClient.apiKeys.revoke(keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useDashboardUsers(q?: { limit?: number; search?: string }) {
  return useQuery({
    queryKey: ["dashboard-users", q?.limit, q?.search],
    queryFn: () => apiClient.dashboard.users(q),
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ["user-stats"],
    queryFn: apiClient.dashboard.userStats,
  });
}

export function useAuditLog(q?: { limit?: number; action?: string }) {
  return useQuery({
    queryKey: ["audit-log", q?.limit, q?.action],
    queryFn: () => apiClient.dashboard.auditLog(q),
  });
}

/** Recent audit events for one provider account — drives the user-detail drawer. */
export function useUserAuditLog(externalUserId: string | undefined) {
  return useQuery({
    queryKey: ["user-audit-log", externalUserId],
    queryFn: () => apiClient.dashboard.auditLog({ externalUserId, limit: 20 }),
    enabled: !!externalUserId,
  });
}

export function useBilling() {
  return useQuery({
    queryKey: ["billing"],
    queryFn: apiClient.billing.summary,
  });
}

export function useUsage() {
  return useQuery({
    queryKey: ["usage"],
    queryFn: apiClient.billing.usage,
  });
}

export function useQuickstart() {
  return useQuery({
    queryKey: ["quickstart"],
    queryFn: apiClient.quickstart.get,
    // Re-fetch when the operator comes back to the tab — checklist
    // progress (e.g. "first auth detected") often updates between
    // visits to this screen.
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}
